import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  // Checkbox, // Batch actions disabled
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  SubdirectoryArrowRight as ReplyIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircleOutline as ResolveIcon,
} from "@mui/icons-material";
import type { ReminderWithAction } from "@services/emailAutomationPipeline/types";

interface ActionCardProps {
  reminder: ReminderWithAction;
  onExecute: () => Promise<void>;
  onResolve?: () => void;
  hideButton?: boolean;
  onGoToShipment?: () => void;
}

const formatMonthDay = (isoDate: string): string => {
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatFullDate = (isoDate: string): string => {
  const d = new Date(isoDate);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const ActionCard: React.FC<ActionCardProps> = ({
  reminder,
  onExecute,
  onResolve,
  hideButton,
  onGoToShipment,
}) => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [executing, setExecuting] = useState(false);

  const data = reminder.data;
  const subject =
    (data.replyToEmailSubject as string) || (data.subject as string) || "";
  const body = (data.messageContent as string) || "";
  const isResolved = reminder.reminderStatus === "resolved";

  const truncateBody = (text: string, maxLen = 100) => {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen) + "...";
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      await onExecute();
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: isResolved ? "grey.100" : "#E3F2FD",
          borderLeft: `3px solid ${isResolved ? "grey.400" : "#1565C0"}`,
          borderRadius: 1,
          mb: 1,
          overflow: "hidden",
        }}
      >
        {/* Header band with reminder name and date */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: isResolved ? "grey.400" : "#1565C0",
            px: 1.5,
            py: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
          >
            {reminder.reminderName}
          </Typography>
          <Tooltip title={formatFullDate(reminder.reminderCreatedAt)} arrow>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
            >
              {formatMonthDay(reminder.reminderCreatedAt)}
            </Typography>
          </Tooltip>
        </Box>

        <Box sx={{ p: 1.5 }}>
          {/* Title row */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {/* Batch actions disabled
            {!isResolved && (
              <Checkbox
                checked={checked}
                onChange={() => onCheckedChange(reminder.actionId)}
                size="small"
                sx={{ mt: -0.5, ml: -0.5, p: 0.5 }}
              />
            )}
            */}
            {reminder.label && (
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {reminder.label}
              </Typography>
            )}
          </Box>

          {reminder.actionType === "draftEmail" && (
            <Box sx={{ ml: 0 }}>
              {/* Subject */}
              {subject && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.25,
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#1565C0",
                      textDecoration: "underline",
                    }}
                  >
                    {subject}
                  </Typography>
                </Box>
              )}

              {/* Body preview */}
              {body && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5,
                    flexWrap: "wrap",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                  >
                    {truncateBody(body)}
                  </Typography>
                  {body.length > 100 && (
                    <Typography
                      variant="caption"
                      onClick={() => setEmailDialogOpen(true)}
                      sx={{
                        color: "#1565C0",
                        textDecoration: "underline",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        "&:hover": { opacity: 0.8 },
                      }}
                    >
                      See full email
                    </Typography>
                  )}
                </Box>
              )}

              {/* Action buttons row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  mt: 0.5,
                }}
              >
                {onGoToShipment && (
                  <Button
                    size="small"
                    startIcon={
                      <OpenInNewIcon sx={{ fontSize: "12px !important" }} />
                    }
                    onClick={onGoToShipment}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.7rem",
                      height: 28,
                      whiteSpace: "nowrap",
                      color: "#1565C0",
                      "& .MuiButton-startIcon": { mr: 0.3 },
                    }}
                  >
                    Go to Shipment
                  </Button>
                )}
                {onGoToShipment && onResolve && !isResolved && (
                  <Box
                    sx={{
                      width: "1px",
                      height: 14,
                      bgcolor: "#1565C0",
                      opacity: 0.4,
                    }}
                  />
                )}
                {onResolve && !isResolved && (
                  <Button
                    size="small"
                    startIcon={
                      <ResolveIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={onResolve}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.7rem",
                      height: 28,
                      whiteSpace: "nowrap",
                      color: "#1565C0",
                      "& .MuiButton-startIcon": { mr: 0.3 },
                    }}
                  >
                    Dismiss
                  </Button>
                )}
                {!hideButton && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      executing ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SendIcon sx={{ fontSize: "14px !important" }} />
                      )
                    }
                    onClick={handleExecute}
                    disabled={executing}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      height: 28,
                      whiteSpace: "nowrap",
                      bgcolor: "#1565C0",
                      flexShrink: 0,
                      "&:hover": { bgcolor: "#0D47A1" },
                    }}
                  >
                    Draft Reply
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Full email dialog */}
      {reminder.actionType === "draftEmail" && body && (
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Email Preview
            </Typography>
            <IconButton size="small" onClick={() => setEmailDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {subject && (
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                {subject}
              </Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {body}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)} size="small">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
